import React from "react";

/**
 * Verixa logo system.
 *
 * - VerixaeLockup: official brand image (V mark + wordmark "Verixae"),
 *   uses /verixae-logo.png with transparent background (auto width).
 *   Preferred wherever the full brand identity is shown.
 *
 * - VerixaMark: inline SVG fallback (V mark only, blue→purple gradient).
 *   Kept for compact spots like favicons, badges, PWA splash, etc.
 *
 * - LogoMark / LogoLockup: legacy wrappers kept to avoid breaking imports.
 */

/* ---------- Official lockup using the uploaded PNG ---------- */
export function VerixaeLockup({ height = 36, className = "", alt = "Verixae", variant = "dark" }) {
  const src = variant === "light" ? "/verixae-logo-light.png" : "/verixae-logo.png";
  return (
    <img
      src={src}
      alt={alt}
      style={{ height, width: "auto", display: "block" }}
      className={`shrink-0 select-none ${className}`}
      draggable={false}
    />
  );
}

/* ---------- Inline SVG V mark (kept for compact uses) ---------- */
export function VerixaMark({ size = 64, className = "", pulse = false }) {
  const id = React.useId().replace(/:/g, "");
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      className={`shrink-0 ${pulse ? "logo-pulse" : ""} ${className}`}
      role="img"
      aria-label="Verixae"
    >
      <defs>
        <linearGradient id={`vBlue-${id}`} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#7DD3FC" />
          <stop offset="45%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#1E40AF" />
        </linearGradient>
        <linearGradient id={`vPurple-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#C084FC" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
      </defs>
      {/* Blue V (front) */}
      <path d="M 32 24 L 66 24 L 100 168 L 84 168 L 32 42 Z" fill={`url(#vBlue-${id})`} />
      <path d="M 84 168 L 100 168 L 138 60 L 122 60 Z" fill={`url(#vBlue-${id})`} opacity="0.95" />
      {/* Purple V (behind, smaller, to the right) */}
      <path d="M 96 32 L 124 32 L 148 132 L 134 132 L 96 48 Z" fill={`url(#vPurple-${id})`} opacity="0.95" />
      <path d="M 134 132 L 148 132 L 168 64 L 156 64 Z" fill={`url(#vPurple-${id})`} opacity="0.9" />
    </svg>
  );
}

/* ---------- Legacy compatibility wrappers ---------- */
export function LogoMark({ size = 64, pulse = false, className = "" }) {
  return (
    <div
      className={`shrink-0 rounded-xl flex items-center justify-center ${pulse ? "logo-pulse" : ""} ${className}`}
      style={{
        width: size,
        height: size,
        background: "linear-gradient(135deg, #0A0E27 0%, #14193A 100%)",
        boxShadow: "0 0 0 1px rgba(59, 130, 246, 0.18), 0 8px 24px -6px rgba(124, 58, 237, 0.35)",
      }}
    >
      <VerixaMark size={Math.round(size * 0.78)} />
    </div>
  );
}

export function Logo({ size = 64, className = "" }) {
  return <VerixaMark size={size} className={className} />;
}

export function LogoLockup({ size = 36, className = "" }) {
  return <VerixaeLockup height={size} className={className} />;
}

export default Logo;
